import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { callPost } from "utils/CommonApi";
import Constants from "common/Constants";
import { NORMALIZE_GRAD_CD } from "components/mypage/myExamInfo/examConstants";

/**
 * 시험정보(시험기간/성적) 등록·수정 화면.
 * 여기서 돌아오면 시험기간이 바뀌었을 수 있어 계획 정보를 다시 읽는다.
 */
const EXAM_INFO_PATHS = ["/mypage/myExamInfo", "/mypage/myExamScore"];

/** 홈(오늘의 학습) 경로. procExamScoreInsYn 은 홈 진입 시에만 보낸다. */
const HOME_PATHS = ["/today/middleStudyToday", "/today/allNewStudyToday", "/today/legacyStudyToday"];

/**
 * 시험대비모드 미노출 이용권. (기획 이용권 표 기준)
 * 서랍(LNB) 노출 조건만으로는 라이트/올탭과외(PEDC-00027~00030)밖에 걸러지지 않아
 * 교문 집합형·프라임 FC·프라임 직영 중등영수처럼 서랍은 나오지만 시험대비 대상이 아닌 이용권을 여기서 제외한다.
 * 제외되면 조회 자체를 하지 않으므로 홈 카드 / 성적 배너 / 플로팅 패널 / 안내 팝업이 모두 나오지 않는다.
 */
const EXAM_PLAN_EXCLUDED_PEDC_CODES = [
    "PEDC-00007", // CMMP 스마트올중학_(심화)수학 (교문 집합형)
    "PEDC-00008", // CMEP 스마트올중학_(심화)영어 (교문 집합형)
    "PEDC-00013", // PFPP 스마트올 중학 프라임 FC
    "PEDC-00015", // PDEP 스마트올 중학 프라임 직영 중등영어
    "PEDC-00016", // PDMP 스마트올 중학 프라임 직영 중등수학
    "PEDC-00027", // LTAL 라이트형 올탭과외
    "PEDC-00028", // ATEN 올탭과외(모과목) 영어
    "PEDC-00029", // ATMT 올탭과외(모과목) 수학
    "PEDC-00030", // ATSC 올탭과외(모과목) 과학
];

/** 시험대비모드 대상이 아닌 이용권인지 */
export const isExamPlanExcludedMember = (pedcCd) => EXAM_PLAN_EXCLUDED_PEDC_CODES.includes(pedcCd);

/** 시험대비모드 기간 종료 후에도 시험 On 모드를 유지하는 기간(일) */
const EXAM_PLAN_AFTER_END_DAYS = 7;

export const getExamPlanPopupStorageKey = (planSeq) => `examPlanIntroPopup_${planSeq || "default"}`;

/** 시험대비 기간(시작 ~ 종료) 중인지 : 메인 카드 / 배너 / 안내 팝업의 노출 기준 */
export const isExamPlanActivePeriod = (examPlanInfo) => {
    const startDDay = Number(examPlanInfo?.START_D_DAY);
    const endDDay = Number(examPlanInfo?.END_D_DAY);

    return Number.isFinite(startDDay) && Number.isFinite(endDDay) && startDDay <= 0 && endDDay >= 0;
};

/** 사전안내 팝업은 운영 시작 30일 전(D-30)부터 노출한다. */
const EXAM_PLAN_NOTICE_START_D_DAY = 30;

/**
 * 사전안내 팝업을 띄우지 않는 학년.
 * 중3은 2학기 기말 시험대비모드 안내팝업이 없다. (성적입력 마감일 변경 공지 팝업으로 대신 안내)
 */
const EXAM_PLAN_NOTICE_EXCLUDED_GRADES = ["GR23"];

/** 시험대비 시작 전 사전안내 기간(D-30 ~ D-1)인지 : 사전안내 팝업의 노출 기준 */
export const isExamPlanNoticePeriod = (examPlanInfo) => {
    const startDDay = Number(examPlanInfo?.START_D_DAY);

    if (EXAM_PLAN_NOTICE_EXCLUDED_GRADES.includes(NORMALIZE_GRAD_CD(examPlanInfo?.GRAD_CD))) return false;

    return Number.isFinite(startDDay) && startDDay > 0 && startDDay <= EXAM_PLAN_NOTICE_START_D_DAY;
};

/**
 * 시험 On 모드(시험대비 영역) 노출 여부.
 * 기간 중에는 항상 노출하고, 기간이 끝난 뒤에는 참여한 회원(JOIN_YN === "Y")에게만
 * EXAM_PLAN_AFTER_END_DAYS 일까지 유지한다. 미참여(N)면 기간 종료와 함께 사라진다.
 */
export const isExamPlanVisible = (examPlanInfo) => {
    if (isExamPlanActivePeriod(examPlanInfo)) return true;

    const startDDay = Number(examPlanInfo?.START_D_DAY);
    const endDDay = Number(examPlanInfo?.END_D_DAY);

    return (
        examPlanInfo?.JOIN_YN === "Y" &&
        Number.isFinite(startDDay) &&
        Number.isFinite(endDDay) &&
        startDDay <= 0 &&
        endDDay >= -EXAM_PLAN_AFTER_END_DAYS
    );
};

/**
 * 성적 입력 마감일. 서버가 함께 내려주는 SCORE_DEADLINE_DT 는 참조하지 않는다.
 * (두 값이 어긋나는 케이스가 있어 기획상 기준값은 SC_DEADLINE_DT 하나다)
 */
export const getExamPlanScoreDeadline = (examPlanInfo) => {
    const raw = examPlanInfo?.SC_DEADLINE_DT;

    if (!raw) return null;

    const date = typeof raw === "number" ? moment(raw) : moment(raw, ["YYYY-MM-DD", "YYYYMMDD"], true);

    return date.isValid() ? date : null;
};

/**
 * 성적 등록 배너 노출 여부. (기획 : 시험대비 기간과 별도로 관리)
 * 시험대비 기간이 끝난 뒤부터 성적 입력 마감일까지만 노출한다.
 */
export const isExamPlanScoreBannerVisible = (examPlanInfo) => {
    const endDDay = Number(examPlanInfo?.END_D_DAY);

    if (!Number.isFinite(endDDay) || endDDay >= 0) return false;

    const deadline = getExamPlanScoreDeadline(examPlanInfo);

    return Boolean(deadline) && moment().isSameOrBefore(deadline, "day");
};

/**
 * 성적 입력 여부 처리(procExamScoreInsYn) 호출 대상인지.
 * 시험대비모드 영역이 더 이상 보이지 않는 상태여도,
 * 사전에 참여한(JOIN_YN === "Y") 회원이면 성적 입력 마감일까지는 서버에 계속 알려줘야 한다.
 * (성적 등록 배너 노출과는 무관한 별도 조건)
 */
export const shouldProcExamScoreInsYn = (examPlanInfo) => {
    if (!examPlanInfo || examPlanInfo.PLAN_SEQ == null) return false;
    if (examPlanInfo.JOIN_YN !== "Y") return false;
    if (isExamPlanVisible(examPlanInfo)) return false;

    const deadline = getExamPlanScoreDeadline(examPlanInfo);

    return Boolean(deadline) && moment().isSameOrBefore(deadline, "day");
};

const formatExamPlanDate = (value) => {
    if (!value) return value;

    const date = typeof value === "number" ? moment(value) : moment(value, ["YYYYMMDD", "YYYY-MM-DD"], true);
    return date.isValid() ? date.format("YYYY-MM-DD") : value;
};

const ExamPlanContext = createContext({
    examPlanInfo: null,
    popupCandidate: null,
    popupInfo: null,
    openRequest: 0,
    refreshExamPlan: () => {},
    openExamPlan: () => {},
    openExamPlanPopup: () => {},
    closeExamPlanPopup: () => {},
    closeExamPlanPopupToday: () => {},
    clearPopupCandidate: () => {},
});

export const useExamPlan = () => useContext(ExamPlanContext);

/**
 * 시험대비모드(시험 On) 정보를 앱 전역에서 한 번만 조회해 공유한다.
 * 플로팅 패널은 서랍이 노출되는 모든 페이지에서 떠야 하는데,
 * 예전에는 홈(StudyToday)이 조회/상태를 모두 들고 있어 홈에서만 볼 수 있었다.
 */
export function ExamPlanProvider({ children }) {
    const userId = useSelector((state) => state.loginReducer.userId);
    const mmbrSeq = useSelector((state) => state.loginReducer.mmbrSeq);
    const pedcCd = useSelector((state) => state.loginReducer.pedcCd);
    const isExcludedMember = isExamPlanExcludedMember(pedcCd);
    const { pathname } = useLocation();

    const [examPlanInfo, setExamPlanInfo] = useState(null);
    /** 진입 안내 팝업 후보. 노출 시점(이벤트 팝업 정리 후)은 홈에서 결정한다. */
    const [popupCandidate, setPopupCandidate] = useState(null);
    /** 현재 열려 있는 진입 안내 팝업 정보 */
    const [popupInfo, setPopupInfo] = useState(null);
    const [openRequest, setOpenRequest] = useState(0);
    /** 조회 콜백 시점의 홈 여부. callPrevMissPlan 의 의존성을 늘리지 않으려고 ref 로 읽는다. */
    const isHomePathRef = useRef(false);
    isHomePathRef.current = HOME_PATHS.some((homePath) => pathname.startsWith(homePath));

    const openExamPlan = useCallback(() => setOpenRequest((request) => request + 1), []);
    const clearPopupCandidate = useCallback(() => setPopupCandidate(null), []);
    const openExamPlanPopup = useCallback(
        (info) => {
            const nextInfo = info || examPlanInfo;

            if (nextInfo) setPopupInfo(nextInfo);
        },
        [examPlanInfo],
    );
    const closeExamPlanPopup = useCallback(() => setPopupInfo(null), []);
    const closeExamPlanPopupToday = useCallback(() => {
        setPopupInfo((currentInfo) => {
            if (currentInfo?.PLAN_SEQ != null) {
                localStorage.setItem(getExamPlanPopupStorageKey(currentInfo.PLAN_SEQ), moment().format("YYYYMMDD"));
            }

            return null;
        });
    }, []);

    const callPrevMissPlan = useCallback(
        (showPopup = true) => {
            if (!mmbrSeq || isExcludedMember) return;

            callPost(
                { user_id: userId },
                {
                    REQUEST_API: Constants.MID_STUDY_API.PREV_MISS_PLAN_INFO,
                    HTTP_METHOD: Constants.HTTP_METHOD.GET,
                },
                (res) => {
                    const missPlanInfo =
                        res?.RESP_RESULT?.MIS_PLAN_INFO || res?.RESP_RESULT?.RESP_RESULT?.MIS_PLAN_INFO;
                    const startDDay = Number(missPlanInfo?.START_D_DAY);

                    if (!missPlanInfo || missPlanInfo.START_D_DAY == null || !Number.isFinite(startDDay)) {
                        setExamPlanInfo(null);
                        setPopupCandidate(null);
                        setPopupInfo(null);
                        return;
                    }

                    let startDDayText = "D - DAY";
                    if (startDDay > 0) {
                        startDDayText = `D - ${startDDay}`;
                    } else if (startDDay < 0) {
                        startDDayText = `D + ${Math.abs(startDDay)}`;
                    }

                    const nextExamPlanInfo = {
                        ...missPlanInfo,
                        SC_DEADLINE_DT: formatExamPlanDate(missPlanInfo.SC_DEADLINE_DT),
                        START_D_DAY_TEXT: startDDayText,
                    };
                    setExamPlanInfo(nextExamPlanInfo);

                    /**
                     * 시험대비모드가 안 보이는 상태라도 참여 회원은 마감일까지 성적 입력 여부를 서버에 처리시킨다.
                     * 기획상 홈 진입 시점 기준이라 홈에서 일어난 조회일 때마다 보낸다. (하루 한 번 제한 없음)
                     */
                    if (isHomePathRef.current && shouldProcExamScoreInsYn(nextExamPlanInfo)) {
                        callPost(
                            { user_id: userId },
                            {
                                REQUEST_API: Constants.MID_STUDY_API.PROC_EXAM_SCORE_INS_YN,
                                HTTP_METHOD: Constants.HTTP_METHOD.POST,
                            },
                            null,
                            { MMBR_SEQ: mmbrSeq, PLAN_SEQ: nextExamPlanInfo.PLAN_SEQ },
                        );
                    }

                    // 시작 전(D-30 ~ D-1)에는 사전안내 팝업, 시험대비 기간 중에는 아직 참여하지 않은 회원에게 진입 안내 팝업을 띄운다.
                    // 둘 다 오늘 그만 보기를 누르지 않았을 때만 노출한다.
                    const popupHiddenToday =
                        localStorage.getItem(getExamPlanPopupStorageKey(missPlanInfo.PLAN_SEQ)) ===
                        moment().format("YYYYMMDD");
                    const hasJoined = missPlanInfo.JOIN_YN === "Y";
                    /**
                     * 사전안내 팝업은 X 로 닫아도 홈에 다시 들어오면 또 띄운다. ('오늘은 그만 볼래요'만 당일 차단)
                     * 그래서 홈에서 다시 읽는 경우(홈 복귀 / 앱 복귀 / 강의 종료 후 갱신)에는 showPopup 과 무관하게 후보로 올린다.
                     */
                    const isNoticePeriod = isExamPlanNoticePeriod(missPlanInfo);
                    /**
                     * showPopup=false 는 화면 복귀 등으로 최신 값만 다시 읽는 경우다.
                     * 이때 대기 중인 안내 팝업 후보를 지워버리면 팝업이 영영 안 뜨므로 건드리지 않는다.
                     */
                    if (!showPopup && !(isNoticePeriod && isHomePathRef.current)) return;

                    const canShowPopup =
                        (isNoticePeriod || (isExamPlanActivePeriod(missPlanInfo) && !hasJoined)) && !popupHiddenToday;

                    setPopupCandidate(canShowPopup ? nextExamPlanInfo : null);
                },
                { MMBR_SEQ: mmbrSeq },
            );
        },
        [isExcludedMember, mmbrSeq, userId],
    );

    const callPrevMissPlanRef = useRef(callPrevMissPlan);
    callPrevMissPlanRef.current = callPrevMissPlan;

    useEffect(() => {
        // 이용권 정보는 로그인 직후 늦게 채워질 수 있어, 대상이 아니게 되면 들고 있던 값도 비운다.
        if (!mmbrSeq || isExcludedMember) {
            setExamPlanInfo(null);
            setPopupCandidate(null);
            setPopupInfo(null);
            return undefined;
        }

        callPrevMissPlanRef.current();

        /**
         * iOS 앱은 화면을 종료하지 않고 백그라운드로만 내려가는 경우가 많아
         * 날짜가 바뀌어도 1회 조회한 결과가 그대로 남아 안내 팝업이 다시 뜨지 않았다.
         * 앱이 다시 보일 때 날짜가 바뀌었으면 시험대비모드 정보를 재조회한다.
         */
        let lastCheckedDate = moment().format("YYYYMMDD");
        const handleVisibilityChange = () => {
            if (document.hidden) return;

            const today = moment().format("YYYYMMDD");

            if (today !== lastCheckedDate) {
                lastCheckedDate = today;
                callPrevMissPlanRef.current();
                return;
            }

            /**
             * 날짜가 그대로여도 화면이 다시 보일 때 한 번 더 읽는다.
             * 시험정보 등록/수정을 네이티브 웹뷰로 띄우면 라우트 변화가 없어
             * 돌아왔을 때 시험기간이 예전 값 그대로 남아 있었다. (안내 팝업은 다시 띄우지 않는다)
             */
            callPrevMissPlanRef.current(false);
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [isExcludedMember, mmbrSeq]);

    /**
     * 시험정보 등록/수정 화면에서 돌아오면 시험기간을 다시 읽는다.
     * 패널이 앱 전역에 상주하면서 화면 이동만으로는 재조회가 일어나지 않아
     * 새로고침 전까지 수정 전 시험기간이 그대로 보였다.
     */
    const previousPathnameRef = useRef(pathname);
    useEffect(() => {
        const previousPathname = previousPathnameRef.current;

        previousPathnameRef.current = pathname;

        if (!mmbrSeq || previousPathname === pathname) return;

        const isFromExamInfo = EXAM_INFO_PATHS.some((examInfoPath) => previousPathname.startsWith(examInfoPath));
        /**
         * 다른 화면에서 홈으로 들어올 때도 다시 읽는다.
         * procExamScoreInsYn 이 홈 진입 시 조회 결과로 판단하는데, 앱 최초 진입 외에는 홈 진입만으로 조회가 일어나지 않았다.
         * (안내 팝업은 다시 띄우지 않는다)
         */
        const isToHome = HOME_PATHS.some((homePath) => pathname.startsWith(homePath));

        if (!isFromExamInfo && !isToHome) return;

        callPrevMissPlanRef.current(false);
    }, [mmbrSeq, pathname]);

    const value = useMemo(
        () => ({
            examPlanInfo,
            popupCandidate,
            popupInfo,
            openRequest,
            refreshExamPlan: callPrevMissPlan,
            openExamPlan,
            openExamPlanPopup,
            closeExamPlanPopup,
            closeExamPlanPopupToday,
            clearPopupCandidate,
        }),
        [
            examPlanInfo,
            popupCandidate,
            popupInfo,
            openRequest,
            callPrevMissPlan,
            openExamPlan,
            openExamPlanPopup,
            closeExamPlanPopup,
            closeExamPlanPopupToday,
            clearPopupCandidate,
        ],
    );

    return <ExamPlanContext.Provider value={value}>{children}</ExamPlanContext.Provider>;
}

export default ExamPlanContext;
